/**
 * Faturas de UM pagamento — o drill-in da tela de Pagamentos.
 *
 * É esta lista que responde ao estado da linha: são as faturas com `paid_by` deste pagamento. Se
 * a soma dos valores recebidos fecha com o pagamento e todas estão pagas, ele está IDENTIFICADO;
 * fechando com faturas abertas, SINALIZADO; qualquer outra coisa, PENDENTE.
 */
import { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaCommentDots, FaTimes, FaTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

import BadgeStatus from '../../../atoms/BadgeStatus';
import AddInvoiceCommentModal from './AddInvoiceCommentModal';
import UnmarkOneInvoiceDialog from './UnmarkOneInvoiceDialog';
import CommentCell from '../../../molecules/CommentCell';
import { PaymentRow, approveIdentification, listInvoicesByPayment } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import { STATUS_META } from '../PaymentsPanel';

const ACTION_BUTTON = 'p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed';

/**
 * Por que aprovar/reprovar está bloqueado nesta linha.
 *
 * As duas ações agem sobre a MARCAÇÃO, e a fatura efetivada já não tem uma — a efetivação apaga a
 * linha da `payment_scheduling`. Dizer isso é diferente de dizer "não pode": o usuário precisa
 * entender que o que ele quer já aconteceu, não que está proibido.
 */
/**
 * O que ainda falta no TÍTULO daquela fatura.
 *
 * Do título e não da fatura: quem confere um pagamento pergunta "esta dívida acabou?", e a fatura
 * sozinha não responde — ela pode ser uma parcela de uma quebra em frações. Vem do bloco do título
 * que a listagem já traz.
 *
 * "—" quando a listagem não trouxe o saldo: um zero ali afirmaria dívida quitada, que é a conclusão
 * mais cara de errar nesta tela.
 */
export const openAmountOf = (inv: any): string =>
    (inv.titleRemainingAmount == null ? '—' : formatBRL(inv.titleRemainingAmount));

/**
 * O valor nominal do TÍTULO daquela fatura — a dívida inteira do charge.
 *
 * ⚠️ Mostrava o `amount` da FATURA, e as duas coisas divergem sempre que o título foi quebrado em
 * frações: uma parcela de 300 de um título de 1.000 aparecia como "nominal 300", ao lado de um "Em
 * Aberto" que fala do título inteiro. Duas colunas vizinhas medindo coisas de tamanhos diferentes,
 * sem nada dizendo isso — e a conta que o usuário faz de cabeça (nominal menos aberto) dava
 * qualquer coisa.
 *
 * "—" sem título eleito (fatura avulsa), como no saldo ao lado: cair no valor da fatura mudaria o
 * SIGNIFICADO da coluna linha a linha, que é o que torna uma coluna não confiável.
 */
export const nominalAmountOf = (inv: any): string =>
    (inv.titleAmount == null ? '—' : formatBRL(inv.titleAmount));

export const blockedReason = (inv: any): string =>
    inv.status === 'PAID'
        ? 'Esta fatura já foi efetivada — não há sinalização a aprovar nem a remover'
        : 'Esta fatura não está sinalizada neste pagamento';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'gold' | 'olive' | 'teal';

const INVOICE_STATUS: Record<string, { label: string; color: BadgeColor }> = {
    OPEN: { label: 'Em aberto', color: 'gold' },
    PAID: { label: 'Paga', color: 'green' },
    CANCELLED: { label: 'Cancelada', color: 'red' },
};

/**
 * O que a linha DIZ quando a fatura está marcada neste pagamento.
 *
 * ⚠️ O status cru mente aqui. Esta é a lista de faturas de UM pagamento, e enquanto ele não for
 * efetivado todas elas estão apenas MARCADAS — dizer "Em aberto" esconde isso, e dizer "Cancelada"
 * é pior ainda: a tela de Identificação marca sempre a fatura ORIGINAL do título, que pode ter sido
 * cancelada numa reemissão, e o usuário fica sem entender onde a marcação dele foi parar.
 *
 * `yellow` é o `bg-yellow-500` — o amarelo de "tem dinheiro apontado, falta aprovar".
 */
const SIGNALLED = { label: 'Pagto Sinalizado', color: 'yellow' as BadgeColor };

interface Props {
    payment: PaymentRow;
    onClose: () => void;
}

interface ModalProps extends Props {
    /** avisa a tela de trás para ela recarregar o dia — aprovar/reprovar aqui muda a linha de lá */
    onChanged?: () => void;
}

const PaymentInvoicesModal: React.FC<ModalProps> = ({ payment, onClose, onChanged }) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    /** id da fatura em que uma ação está em curso — trava a tabela inteira, não só a linha */
    const [busy, setBusy] = useState<string | null>(null);
    const [commenting, setCommenting] = useState<any>(null);
    const [rejecting, setRejecting] = useState<any>(null);

    const reload = useCallback(async () => {
        try {
            setRows(await listInvoicesByPayment(payment.paymentId));
        } catch { setRows([]); }
        setLoading(false);
    }, [payment.paymentId]);

    useEffect(() => { reload(); }, [reload]);

    /**
     * Efetiva UMA fatura deste pagamento.
     *
     * ⚠️ Sem confirmação, ao contrário do reprovar. Aprovar é o caminho ESPERADO — o financeiro
     * está aqui justamente para isso — e o efeito é o mesmo do botão da tela de trás, que também
     * não pergunta. Reprovar é que precisa de motivo, e o motivo é a confirmação.
     */
    const approveOne = async (inv: any) => {
        setBusy(inv.invoiceId);
        try {
            await approveIdentification(payment.paymentId, [inv.invoiceId]);
            toast.success('Identificação aprovada. A fatura foi liquidada.');
            await reload();
            onChanged?.();
        } catch (e: any) {
            toast.error(`Não foi possível aprovar: ${e.message}`);
        } finally {
            setBusy(null);
        }
    };

    const total = Number(payment.amount || 0);
    const sinalizado = rows.reduce((s, inv) => s + Number(inv.amountPaid || 0), 0);
    /**
     * A conta só interessa a quem ainda tem trabalho pela frente. Em IDENTIFICADO ela já fechou
     * por definição — repetir o número ali é ruído.
     */
    const mostraConta = payment.status !== 'IDENTIFIED';
    /**
     * "faltam X" só quando falta ALGO de fato. Com zero marcado, dizer "faltam o valor inteiro" é
     * repetir o que a linha de cima já diz; com a conta fechada, não falta nada.
     */
    const mostraFalta = sinalizado > 0 && Math.abs(sinalizado - total) >= 0.005;

    return (
        <>
            {/* ⚠️ O overlay fecha no clique, e por isso os modais FILHOS não moram dentro dele.
            Dentro, qualquer clique neles — inclusive focar o campo de texto — borbulhava até este
            `onClick` e fechava os dois de uma vez. Irmãos no mesmo fragmento, o clique do filho
            nunca passa por aqui. */}
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-[96rem] max-h-[90vh] flex flex-col"
                    onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                        <div>
                            <h3 className="text-lg font-semibold">Faturas do pagamento</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                #{payment.paymentId.slice(0, 8)}
                                {' · '}{STATUS_META[payment.status].label}
                                {' · '}valor {formatBRL(payment.amount || 0)}
                            </p>
                        </div>
                        <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>

                    <div className="px-6 py-4 overflow-y-auto">
                        {loading ? (
                            <p className="text-center text-gray-500 py-6">Carregando...</p>
                        ) : rows.length === 0 ? (
                            <p className="text-center text-gray-500 py-6">
                                Nenhuma fatura sinalizada neste pagamento ainda.
                            </p>
                        ) : (
                            <>
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 dark:bg-slate-700">
                                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                            <th className="px-3 py-2">Fatura</th>
                                            <th className="px-3 py-2">Contrato</th>
                                            <th className="px-3 py-2">Título/NF</th>
                                            {/* Três valores lado a lado, do que foi cobrado ao que
                                            falta: o nominal da fatura, o que este pagamento aponta
                                            para ela, e o saldo do TÍTULO. "Valor"/"Recebido" eram
                                            ambíguos — o primeiro não dizia de quê, e o segundo
                                            afirmava recebimento numa fatura que pode estar só
                                            sinalizada. */}
                                            <th className="px-3 py-2 text-right">Valor Nominal</th>
                                            <th className="px-3 py-2 text-right">Em Aberto</th>
                                            <th className="px-3 py-2 text-right">Sinalizado / Recebido</th>
                                            <th className="px-3 py-2 text-center">Vencimento</th>
                                            <th className="px-3 py-2 text-center">Status</th>
                                            {/* entre Status e Ações, como na tela de Faturas */}
                                            <th className="px-3 py-2">Comentários</th>
                                            <th className="px-3 py-2 text-center">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((inv: any) => {
                                            // marcada vence o status cru: PAGA nunca chega aqui
                                            // marcada (a efetivação apaga a marcação)
                                            const st = inv.paymentSignalled ? SIGNALLED
                                                : INVOICE_STATUS[inv.status]
                                                || { label: inv.status, color: 'gray' as BadgeColor };
                                            return (
                                                <tr key={inv.invoiceId} className="border-t dark:border-slate-600">
                                                    <td className="px-3 py-2 text-xs font-mono text-gray-500" title={inv.invoiceId}>
                                                        {String(inv.invoiceId).slice(0, 8)}
                                                    </td>
                                                    <td className="px-3 py-2">{inv.contractLabel || '-'}</td>
                                                    <td className="px-3 py-2">{inv.details?.documentTitle || '-'}</td>
                                                    <td className="px-3 py-2 text-right">{nominalAmountOf(inv)}</td>
                                                    <td className="px-3 py-2 text-right">{openAmountOf(inv)}</td>
                                                    {/* A cor diz em que passo o dinheiro está, sem
                                                    obrigar a cruzar com a coluna de Status: âmbar é
                                                    promessa (sinalizado, esperando aprovação),
                                                    verde é fato (efetivado). É a MESMA convenção da
                                                    linha da tela de Pagamentos e das fatias da
                                                    barra do passo 3. */}
                                                    <td className={`px-3 py-2 text-right font-medium ${inv.paymentSignalled
                                                        ? 'text-yellow-600'
                                                        : inv.status === 'PAID' ? 'text-green-600' : ''}`}>
                                                        {formatBRL(inv.amountPaid || 0)}
                                                    </td>
                                                    <td className="px-3 py-2 text-center max-w-[110px]">{formatDateBR(inv.dueDate)}</td>
                                                    <td className="px-3 py-2 text-center max-w-[132px]">
                                                        <BadgeStatus text={st.label} color={st.color} />
                                                    </td>
                                                    <td className="px-3 py-2 max-w-[220px]">
                                                        <CommentCell notes={inv.details?.notesList || []} />
                                                    </td>
                                                    <td className="px-3 py-2 text-center whitespace-nowrap">
                                                        {/* Aprovar e reprovar só nas MARCADAS: a
                                                        efetivada já saiu da payment_scheduling e não
                                                        há o que efetivar nem o que desfazer nela.
                                                        `<span title>` em volta porque um <button
                                                        disabled> engole o tooltip nativo em alguns
                                                        navegadores — e é justamente no desabilitado
                                                        que o motivo importa. */}
                                                        <span title={inv.paymentSignalled
                                                            ? 'Aprovar a identificação desta fatura'
                                                            : blockedReason(inv)}>
                                                            <button
                                                                onClick={() => approveOne(inv)}
                                                                disabled={!inv.paymentSignalled || !!busy}
                                                                aria-label={`Aprovar identificação da fatura ${String(inv.invoiceId).slice(0, 8)}`}
                                                                className={`${ACTION_BUTTON} text-green-600 hover:text-green-700 mr-2`}>
                                                                <FaCheck />
                                                            </button>
                                                        </span>
                                                        <span title={inv.paymentSignalled
                                                            ? 'Reprovar: remove a sinalização desta fatura'
                                                            : blockedReason(inv)}>
                                                            <button
                                                                onClick={() => setRejecting(inv)}
                                                                disabled={!inv.paymentSignalled || !!busy}
                                                                aria-label={`Reprovar identificação da fatura ${String(inv.invoiceId).slice(0, 8)}`}
                                                                className={`${ACTION_BUTTON} text-red-600 hover:text-red-700 mr-2`}>
                                                                <FaTimesCircle />
                                                            </button>
                                                        </span>
                                                        {/* comentar vale em qualquer estado */}
                                                        <button
                                                            onClick={() => setCommenting(inv)}
                                                            aria-label={`Adicionar comentário à fatura ${String(inv.invoiceId).slice(0, 8)}`}
                                                            title="Adicionar comentário"
                                                            className={`${ACTION_BUTTON} text-blue-600 hover:text-blue-700`}>
                                                            <FaCommentDots />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* a conta é o que decide o estado do pagamento — mostrá-la evita que
                                o usuário tenha de somar a coluna com o olho para entender por que
                                a linha ainda está pendente */}
                                {mostraConta && (
                                    <div className="mt-4 px-3 py-2 text-sm rounded bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                        Sinalizado: <strong>{formatBRL(sinalizado)}</strong> de{' '}
                                        <strong>{formatBRL(total)}</strong>
                                        {mostraFalta && <> — faltam {formatBRL(total - sinalizado)}.</>}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {commenting && (
                <AddInvoiceCommentModal
                    invoice={commenting}
                    onClose={() => setCommenting(null)}
                    onAdded={reload}
                />
            )}
            {rejecting && (
                <UnmarkOneInvoiceDialog
                    payment={payment}
                    invoice={rejecting}
                    onClose={() => setRejecting(null)}
                    onUnmarked={() => { reload(); onChanged?.(); }}
                />
            )}
        </>
    );
};

export default PaymentInvoicesModal;
